import React, { useEffect, useState } from "react";
import { getApiErrorMessage } from "../api/client";
import {
  confirmForgotPasswordReset,
  fetchAuthProviders,
  getPortalLoginUrl,
  login,
  requestForgotPasswordReset,
} from "../api";
import "./LoginPage.css";

const loginFontsHref =
  "https://fonts.googleapis.com/css2?family=Merriweather:wght@700&family=Source+Sans+3:wght@400;600;700;800&display=swap";

const existingFontLink = document.head.querySelector(`link[href="${loginFontsHref}"]`);
if (!existingFontLink) {
  const fontLink = document.createElement("link");
  fontLink.href = loginFontsHref;
  fontLink.rel = "stylesheet";
  document.head.appendChild(fontLink);
}

const KURUM_LOGO_SRC = `${String(process.env.PUBLIC_URL || "").replace(/\/$/, "")}/kurum-logo.svg`;
const REMEMBER_PASSWORD_KEY = "geoRememberPassword_v1";
const REMEMBERED_USERNAME_KEY = "geoRememberedUsername_v1";
const PASSWORD_RULES_TEXT = "Şifre boş bırakılamaz.";

function getRoleLabel(role) {
  if (role === "project_admin") return "Yönetici";
  if (role === "superadmin") return "Süper Admin";
  return "Personel";
}

function normalizeAuthorizedProjects(projects) {
  const uniqueById = new Map();

  for (const project of projects || []) {
    const projectId = project?.projectId;
    if (!projectId) continue;
    if (!uniqueById.has(projectId)) {
      uniqueById.set(projectId, project);
    }
  }

  return Array.from(uniqueById.values()).sort((left, right) =>
    String(left?.projectName || "").localeCompare(String(right?.projectName || ""), "tr")
  );
}

export default function LoginPage({ onLogin }) {
  const [username, setUsername] = useState(() => {
    const remembered = localStorage.getItem(REMEMBER_PASSWORD_KEY) === "1";
    if (!remembered) return "";
    return localStorage.getItem(REMEMBERED_USERNAME_KEY) || "";
  });
  const [password, setPassword] = useState("");
  const [rememberPassword, setRememberPassword] = useState(
    () => localStorage.getItem(REMEMBER_PASSWORD_KEY) === "1"
  );
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotResetCode, setForgotResetCode] = useState("");
  const [forgotPasswordValue, setForgotPasswordValue] = useState("");
  const [forgotPasswordConfirm, setForgotPasswordConfirm] = useState("");
  const [forgotNotice, setForgotNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [tempUser, setTempUser] = useState(null);
  const [authorizedProjects, setAuthorizedProjects] = useState([]);
  const [logoUnavailable, setLogoUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchAuthProviders()
      .then((providers) => {
        if (!cancelled && providers?.portalEnabled) {
          window.location.replace(getPortalLoginUrl());
        }
      })
      .catch(() => {
        // Geliştirme veya geçici bağlantı hatasında yerel form kullanılabilir kalır.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setForgotNotice("");
    setLoading(true);

    try {
      const normalizedUsername = username.trim().toLowerCase();
      if (rememberPassword) {
        localStorage.setItem(REMEMBER_PASSWORD_KEY, "1");
        localStorage.setItem(REMEMBERED_USERNAME_KEY, normalizedUsername);
      } else {
        localStorage.setItem(REMEMBER_PASSWORD_KEY, "0");
        localStorage.removeItem(REMEMBERED_USERNAME_KEY);
      }

      const response = await login(normalizedUsername, password, {
        remember: rememberPassword,
      });
      const nextUser = response?.user || null;
      const nextProjects = normalizeAuthorizedProjects(
        response?.authorizedProjects || nextUser?.authorizedProjects || []
      );

      if (!nextUser) {
        setError("Oturum başlatılamadı.");
        return;
      }

      if (
        nextProjects.length === 1 &&
        nextProjects[0].projectId === "ALL" &&
        nextProjects[0].role === "superadmin"
      ) {
        onLogin(
          {
            ...nextUser,
            role: "superadmin",
            projectId: null,
          },
          { remember: rememberPassword }
        );
        return;
      }

      if (nextProjects.length === 1) {
        onLogin(
          {
            ...nextUser,
            role: nextProjects[0].role,
            projectId: nextProjects[0].projectId,
          },
          { remember: rememberPassword }
        );
        return;
      }

      if (nextProjects.length > 1) {
        setTempUser(nextUser);
        setAuthorizedProjects(nextProjects);
        setStep(2);
        return;
      }

      setError("Hesabınıza atanmış aktif bir proje bulunamadı.");
    } catch (apiError) {
      setError(getApiErrorMessage(apiError, "Giriş yapılamadı."));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProject = (project) => {
    if (!tempUser) return;

    onLogin(
      {
        ...tempUser,
        role: project.role,
        projectId: project.projectId,
      },
      { remember: rememberPassword }
    );
  };

  const resetForgotPasswordState = () => {
    setForgotResetCode("");
    setForgotPasswordValue("");
    setForgotPasswordConfirm("");
    setForgotNotice("");
    setError("");
  };

  const handleBackToLogin = () => {
    setStep(1);
    setAuthorizedProjects([]);
    setTempUser(null);
    setPassword("");
    resetForgotPasswordState();
  };

  const handleOpenForgotPassword = () => {
    setForgotUsername(username.trim().toLowerCase());
    setForgotEmail("");
    resetForgotPasswordState();
    setStep(3);
  };

  const handleBackToForgotRequest = () => {
    resetForgotPasswordState();
    setStep(3);
  };

  const handleForgotRequestSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setForgotNotice("");

    const normalizedUsername = forgotUsername.trim().toLowerCase();
    const normalizedEmail = forgotEmail.trim().toLowerCase();

    if (!normalizedUsername || !normalizedEmail) {
      setError("Kullanıcı adı ve e-posta zorunludur.");
      return;
    }

    setLoading(true);
    try {
      const response = await requestForgotPasswordReset(
        normalizedUsername,
        normalizedEmail
      );
      const baseMessage =
        response?.message ||
        "Hesap eşleşirse doğrulama kodu e-posta adresinize gönderildi.";
      if (response?.debugResetCode) {
        setForgotNotice(`${baseMessage} Test kodu: ${response.debugResetCode}`);
        setForgotResetCode(response.debugResetCode);
      } else {
        setForgotNotice(baseMessage);
        setForgotResetCode("");
      }
      setStep(4);
    } catch (apiError) {
      setError(getApiErrorMessage(apiError, "Doğrulama kodu gönderilemedi."));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotConfirmSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setForgotNotice("");

    const normalizedUsername = forgotUsername.trim().toLowerCase();
    const normalizedEmail = forgotEmail.trim().toLowerCase();
    const normalizedCode = forgotResetCode.trim().toUpperCase();
    const nextPassword = forgotPasswordValue;
    const nextPasswordConfirm = forgotPasswordConfirm;

    if (!normalizedUsername || !normalizedEmail || !normalizedCode || !nextPassword) {
      setError("Kullanıcı adı, e-posta, doğrulama kodu ve yeni şifre zorunludur.");
      return;
    }

    if (nextPassword !== nextPasswordConfirm) {
      setError("Yeni şifre ve tekrar şifre eşleşmiyor.");
      return;
    }

    setLoading(true);
    try {
      const response = await confirmForgotPasswordReset(
        normalizedUsername,
        normalizedEmail,
        normalizedCode,
        nextPassword
      );
      setForgotNotice(
        response?.message ||
          "Şifreniz güncellendi. Yeni şifrenizle giriş yapabilirsiniz."
      );
      setForgotResetCode("");
      setForgotPasswordValue("");
      setForgotPasswordConfirm("");
      setStep(3);
    } catch (apiError) {
      setError(getApiErrorMessage(apiError, "Şifre yenilenemedi."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-shape login-shape-water" aria-hidden="true" />
      <div className="login-shape login-shape-land" aria-hidden="true" />

      <div className="login-shell">
        <aside className="login-showcase" aria-hidden="true">
          <p className="showcase-badge">Pafta Yönetim Sistemi</p>
          <h1 className="showcase-title">Yer Bilimleri ve Çevre İzleme Platformu</h1>
          <p className="showcase-text">
            Su kaynakları, saha ölçümleri ve çevresel veriler tek platformda güvenli
            şekilde yönetilir.
          </p>
          <ul className="showcase-list">
            <li>Havza ve su noktası takibi</li>
            <li>Proje bazlı yetkilendirme</li>
            <li>İzlenebilir ve denetlenebilir kayıt yapısı</li>
          </ul>
        </aside>

        <section className="login-card">
          <div className="login-header">
            <div className="institution-banner">
              <div className="institution-text">
                <p className="institution-name">Maden Tetkik ve Arama Genel Müdürlüğü</p>
                <p className="institution-department">
                  Çevre Araştırmaları Dairesi Başkanlığı
                </p>
              </div>
              <div className="logo-container">
                {logoUnavailable ? (
                  <div className="logo-fallback" aria-hidden="true">
                    MTA
                  </div>
                ) : (
                  <img
                    src={KURUM_LOGO_SRC}
                    alt="MTA logosu"
                    className="logo-image"
                    onError={() => setLogoUnavailable(true)}
                  />
                )}
              </div>
            </div>

            <h2 className="login-title">Yetkili Kullanıcı Girişi</h2>
            <p className="login-subtitle">
              Devam etmek için kullanıcı bilgilerinizi giriniz.
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSubmit} className="login-form">
              {error ? <div className="error-message">{error}</div> : null}

              <div className="form-group">
                <label htmlFor="username">Kullanıcı Adı</label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                  placeholder="Kullanıcı adınızı giriniz"
                  required
                  autoFocus
                  autoComplete="username"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Şifre</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Şifrenizi giriniz"
                  required
                  autoComplete="current-password"
                  disabled={loading}
                />
              </div>

              <label className="remember-password-row">
                <input
                  type="checkbox"
                  checked={rememberPassword}
                  onChange={(event) => setRememberPassword(event.target.checked)}
                  disabled={loading}
                />
                <span>Şifremi Hatırla</span>
              </label>

              <button
                type="submit"
                className="login-button"
                disabled={loading || !username || !password}
              >
                {loading ? "Giriş yapılıyor..." : "Sisteme Giriş Yap"}
              </button>
              <button
                type="button"
                className="login-inline-link"
                onClick={handleOpenForgotPassword}
                disabled={loading}
              >
                Şifremi Unuttum
              </button>
            </form>
          ) : null}

          {step === 2 ? (
            <div className="project-picker-overlay" role="dialog" aria-modal="true">
              <div className="project-selection-modal">
                <h3 className="project-selection-title">
                  Lütfen çalışacağınız projeyi seçiniz
                </h3>
                <p className="project-selection-subtitle">
                  Hesabınız birden fazla projede yetkili. Devam etmek için bir proje seçin.
                </p>

                <div className="project-list">
                  {authorizedProjects.map((project) => (
                    <button
                      type="button"
                      key={project.projectId}
                      onClick={() => handleSelectProject(project)}
                      className="project-item-button"
                    >
                      <span>{project.projectName}</span>
                      <span className="project-role">{getRoleLabel(project.role)}</span>
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  className="project-back-button"
                  onClick={handleBackToLogin}
                >
                  Geri Dön
                </button>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <form onSubmit={handleForgotRequestSubmit} className="login-form">
              {error ? <div className="error-message">{error}</div> : null}
              {forgotNotice ? <div className="notice-message">{forgotNotice}</div> : null}

              <div className="form-group">
                <label htmlFor="forgot-username">Kullanıcı Adı</label>
                <input
                  id="forgot-username"
                  type="text"
                  value={forgotUsername}
                  onChange={(event) => setForgotUsername(event.target.value)}
                  placeholder="Kullanıcı adınızı giriniz"
                  autoComplete="username"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="forgot-email">E-posta</label>
                <input
                  id="forgot-email"
                  type="email"
                  value={forgotEmail}
                  onChange={(event) => setForgotEmail(event.target.value)}
                  placeholder="Kayıtlı e-posta adresiniz"
                  autoComplete="email"
                  disabled={loading}
                  required
                />
              </div>

              <button
                type="submit"
                className="login-button"
                disabled={loading || !forgotUsername.trim() || !forgotEmail.trim()}
              >
                {loading ? "Kod gönderiliyor..." : "Doğrulama Kodu Gönder"}
              </button>

              <button
                type="button"
                className="login-inline-link"
                onClick={handleBackToLogin}
                disabled={loading}
              >
                Giriş Ekranına Dön
              </button>
            </form>
          ) : null}

          {step === 4 ? (
            <form onSubmit={handleForgotConfirmSubmit} className="login-form">
              {error ? <div className="error-message">{error}</div> : null}
              {forgotNotice ? <div className="notice-message">{forgotNotice}</div> : null}

              <div className="form-group">
                <label htmlFor="forgot-reset-code">Doğrulama Kodu</label>
                <input
                  id="forgot-reset-code"
                  type="text"
                  value={forgotResetCode}
                  onChange={(event) =>
                    setForgotResetCode(event.target.value.toUpperCase())
                  }
                  placeholder="E-posta ile gelen kod"
                  autoComplete="one-time-code"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="forgot-password">Yeni Şifre</label>
                <input
                  id="forgot-password"
                  type="password"
                  value={forgotPasswordValue}
                  onChange={(event) => setForgotPasswordValue(event.target.value)}
                  placeholder="Yeni şifrenizi giriniz"
                  autoComplete="new-password"
                  disabled={loading}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="forgot-password-confirm">Yeni Şifre (Tekrar)</label>
                <input
                  id="forgot-password-confirm"
                  type="password"
                  value={forgotPasswordConfirm}
                  onChange={(event) => setForgotPasswordConfirm(event.target.value)}
                  placeholder="Yeni şifrenizi tekrar giriniz"
                  autoComplete="new-password"
                  disabled={loading}
                  required
                />
              </div>

              <p className="login-subtitle" style={{ marginTop: -4 }}>
                {PASSWORD_RULES_TEXT}
              </p>

              <button
                type="submit"
                className="login-button"
                disabled={
                  loading ||
                  !forgotResetCode.trim() ||
                  !forgotPasswordValue ||
                  !forgotPasswordConfirm
                }
              >
                {loading ? "Şifre yenileniyor..." : "Şifreyi Yenile"}
              </button>

              <button
                type="button"
                className="login-inline-link"
                onClick={handleBackToForgotRequest}
                disabled={loading}
              >
                Kod İstek Ekranına Dön
              </button>
            </form>
          ) : null}

          <div className="login-footer">
            <p className="login-footer-note">
              Bu sistem Maden Tetkik ve Arama Genel Müdürlüğü Çevre Araştırmaları
              Dairesi Başkanlığı tarafından kullanılmaktadır.
            </p>
            <p className="login-copyright">
              © 2026 Maden Tetkik ve Arama Genel Müdürlüğü. Tüm hakları saklıdır.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
